/**
 * Trees — árvores low-poly instanciadas (cinturão de floresta, parques e
 * canteiros junto aos prédios). Cada árvore é uma copa "cheia" (cluster de
 * lóbulos arredondados) com gradiente vertical do escuro (base) ao claro
 * (topo), mais um tronco. Vistas de cima, leem como arbustos verdes lush.
 */

import * as THREE from 'three';
import { TEX_TIER } from '../scene/qualityLevel.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { COLORS, WORLD } from '../config.js';
import { generateTrees, makeRandom } from './cityLayout.js';

// Interação: arbustos reagem ao serem "esbarrados" por pessoas — inclinam no
// sentido da colisão (pra longe da pessoa) e voltam com uma molinha (wobble).
const CELL = 4;          // célula do grid espacial (>= maior raio de contato)
// Varredura de CONTATO a 10 Hz. A mola continua integrando a 60 Hz, então o
// movimento do arbusto segue suave — o que fica mais grosso é só a taxa com que o
// ALVO do empurrão é recalculado. Medido: a varredura custava 0,215 ms por quadro
// com 250 pessoas (3× todo o pipeline de rótulos); a 10 Hz cai para ~0,036 ms.
// Custo: um contato pode ser notado até 100 ms depois — a ~6 u/s são 0,6 u de
// atraso num raio de contato de ~1,5–2 u, absorvido pela mola.
const SCAN_HZ = 10;
const SCAN_DT = 1 / SCAN_HZ;
const PERSON_R = 1.0;    // "largura" da pessoa para o contato
const CANOPY_R = 1.35;   // alcance da copa por unidade de escala
const SPRING_K = 70;     // rigidez da mola (volta mais rápido = maior)
const SPRING_C = 10;     // amortecimento (wobble: menor = mais balanço)

export function createTrees() {
  const group = new THREE.Group();
  group.name = 'trees';

  const rand = makeRandom(99001);
  const trees = generateTrees(WORLD.width, WORLD.depth, rand);
  const n = trees.length;

  // ---- copa: cluster de lóbulos com gradiente vertical assado nos vértices
  //
  // MATERIAL LAMBERT (era Standard com roughness 0.95 / metalness 0). Numa
  // superfície tão fosca e sem metal, o termo especular do PBR é ~4% de Fresnel
  // espalhado numa lobe larguíssima — praticamente invisível. Mas ele CUSTA o BRDF
  // GGX completo por pixel, mais o espelhamento indireto (que roda mesmo sem
  // envMap). E as copas são o maior consumidor de pixels da cena: ~2,4k copas
  // somam cerca de 1,7× a área da tela (com sobreposição). Lambert é só
  // `dotNL × cor`. Gradiente de vértice, tint por instância e o brilho `emissive`
  // continuam idênticos — o Lambert suporta os três.
  const canopyMat = new THREE.MeshLambertMaterial({
    vertexColors: true,
    emissive: new THREE.Color('#0a3a1d'),
    emissiveIntensity: 0.35
  });
  // DUAS copas (ver LOD_SCALE): a cheia e a simplificada. O material é o mesmo, e
  // cada uma é um InstancedMesh — 2 draw calls onde antes era 1. Trocado por
  // 125 mil triângulos a menos.
  //
  // O índice `i` do arbusto continua sendo a chave de TUDO (a grade espacial
  // gHead/gNext, os arrays de mola, o tronco). Como as duas copas têm listas de
  // instância próprias, cada arbusto guarda em qual copa está (`cLod`) e em que
  // linha dela (`cRow`) — sem isso, separar as copas corromperia o índice.
  const quantos = [0, 0];
  const cLod = new Uint8Array(n);
  const cRow = new Int32Array(n);
  for (let i = 0; i < n; i++) {
    const lod = trees[i].scale < LOD_SCALE ? 1 : 0;
    cLod[i] = lod;
    cRow[i] = quantos[lod]++;
  }
  // a copa "pobre" é a ÚNICA geometria facetada de verdade da cena (detail 0 tem
  // normais planas, e ~1/4 dos arbustos usa ela): no nível Ultra ela volta a
  // detail 1 e o facetamento some — ver TEX_TIER.canopyLodDetail.
  const canopyGeos = [buildCanopyGeometry(CANOPY_DETAIL), buildCanopyGeometry(TEX_TIER.canopyLodDetail)];
  const canopies = [
    new THREE.InstancedMesh(canopyGeos[0], canopyMat, Math.max(1, quantos[0])),
    new THREE.InstancedMesh(canopyGeos[1], canopyMat, Math.max(1, quantos[1]))
  ];
  for (let k = 0; k < 2; k++) {
    canopies[k].count = quantos[k];
    canopies[k].castShadow = true;
    canopies[k].receiveShadow = true;
    canopies[k].name = k ? 'canopyLod' : 'canopy';
  }

  // ---- tronco
  // SEM TAMPAS (`openEnded`). A tampa de CIMA fica dentro da copa: verificado
  // contra o poliedro exato do lóbulo grande (80 planos, não a esfera), 61 pontos
  // da borda testados, margem mínima 0,809u = 7,2 px. A tampa de BAIXO é
  // back-facing (o material é FrontSide) e some no backface culling. As duas somam
  // 12 dos 24 triângulos do tronco: 28.680 triângulos a menos na cidade (2,87%),
  // sem custo de runtime e sem mudança de silhueta.
  // Ressalva sub-pixel: no /me?follow (maxPolar 1,4 rad) com o arbusto inclinado
  // no máximo, a borda de baixo sobe ~0,10u = 0,9 px, transitoriamente.
  const trunkGeo = new THREE.CylinderGeometry(0.22, 0.32, 1.8, 6, 1, true);
  trunkGeo.translate(0, 0.9, 0);
  // roughness 1.0 + metalness 0 é, matematicamente, o caso em que o PBR mais se
  // aproxima de Lambert — a troca aqui não muda pixel perceptível.
  const trunkMat = new THREE.MeshLambertMaterial({ color: COLORS.treeTrunk });
  const trunk = new THREE.InstancedMesh(trunkGeo, trunkMat, n);
  trunk.castShadow = true;

  const dummy = new THREE.Object3D();
  const col = new THREE.Color();

  // estado por arbusto p/ a interação (posição de repouso + mola da inclinação)
  const baseX = new Float32Array(n);
  const baseZ = new Float32Array(n);
  const baseScale = new Float32Array(n);
  const baseRot = new Float32Array(n);
  const radius = new Float32Array(n);   // raio de contato
  const maxAngle = new Float32Array(n); // inclinação máxima (rad)
  const leanX = new Float32Array(n), leanZ = new Float32Array(n); // inclinação atual
  const velX = new Float32Array(n), velZ = new Float32Array(n);   // velocidade da mola
  const tgtX = new Float32Array(n), tgtZ = new Float32Array(n);   // alvo (empurrão)
  // Alvo da varredura ANTERIOR + marca de "já parou nele". Existem porque uma pessoa
  // PARADA encostada num arbusto produz o MESMO empurrão varredura após varredura: sem
  // isto o arbusto ficava eternamente ativo, e um único ativo faz o update reenviar a
  // matriz das TRÊS malhas de vegetação inteiras (4.346 instâncias, 272 KB) todo quadro.
  const prevX = new Float32Array(n), prevZ = new Float32Array(n);
  const assentado = new Uint8Array(n);
  const radius2 = new Float32Array(n); // raio² — evita a raiz quadrada na rejeição

  for (let i = 0; i < n; i++) {
    const t = trees[i];
    const s = t.scale;

    dummy.position.set(t.x, 0, t.z);
    dummy.rotation.set(0, t.rot, 0);
    dummy.scale.setScalar(s);
    dummy.updateMatrix();
    canopies[cLod[i]].setMatrixAt(cRow[i], dummy.matrix);
    trunk.setMatrixAt(i, dummy.matrix);

    // tint por instância (multiplica o gradiente assado na copa)
    col.set(t.tint);
    canopies[cLod[i]].setColorAt(cRow[i], col);

    baseX[i] = t.x; baseZ[i] = t.z; baseScale[i] = s; baseRot[i] = t.rot;
    radius[i] = s * CANOPY_R + PERSON_R;
    radius2[i] = radius[i] * radius[i];
    maxAngle[i] = Math.min(0.55, 0.32 / s); // arbusto pequeno balança mais que árvore grande
  }

  // GRADE ESPACIAL em arrays PLANOS (era um Map com chave de string "cx,cz").
  // Concatenar string e hashear 9 chaves por pessoa por quadro era trabalho puro:
  // aqui a célula é um índice inteiro e a lista de arbustos é uma lista ligada em
  // Int32Array — sem alocação, sem hash, sem string.
  let gMinX = Infinity, gMaxX = -Infinity, gMinZ = Infinity, gMaxZ = -Infinity;
  for (let i = 0; i < n; i++) {
    if (baseX[i] < gMinX) gMinX = baseX[i];
    if (baseX[i] > gMaxX) gMaxX = baseX[i];
    if (baseZ[i] < gMinZ) gMinZ = baseZ[i];
    if (baseZ[i] > gMaxZ) gMaxZ = baseZ[i];
  }
  const gx0 = Math.floor(gMinX / CELL) - 1, gz0 = Math.floor(gMinZ / CELL) - 1;
  const gCols = Math.floor(gMaxX / CELL) + 2 - gx0;
  const gRows = Math.floor(gMaxZ / CELL) + 2 - gz0;
  const gHead = new Int32Array(gCols * gRows).fill(-1);
  const gNext = new Int32Array(n).fill(-1);
  for (let i = 0; i < n; i++) {
    const cx = Math.floor(baseX[i] / CELL) - gx0;
    const cz = Math.floor(baseZ[i] / CELL) - gz0;
    const c = cx * gRows + cz;
    gNext[i] = gHead[c];
    gHead[c] = i;
  }
  for (const c of canopies) {
    c.instanceMatrix.needsUpdate = true;
    if (c.instanceColor) c.instanceColor.needsUpdate = true;
  }
  trunk.instanceMatrix.needsUpdate = true;

  group.add(trunk, canopies[0], canopies[1]);

  /* ----------------------- interação (mola por arbusto) ----------------------- */
  const active = new Set(); // só arbustos "mexidos" são integrados/atualizados
  const toRemove = [];
  const _axis = new THREE.Vector3();
  const _qLean = new THREE.Quaternion();
  const _qRot = new THREE.Quaternion();
  const _Y = new THREE.Vector3(0, 1, 0);

  const writeMatrix = (i) => {
    const Lx = leanX[i], Lz = leanZ[i];
    const mag = Math.sqrt(Lx * Lx + Lz * Lz); // hypot é bem mais caro no V8
    dummy.position.set(baseX[i], 0, baseZ[i]);
    dummy.scale.setScalar(baseScale[i]);
    if (mag < 1e-4) {
      dummy.quaternion.setFromAxisAngle(_Y, baseRot[i]);
    } else {
      // inclina a copa NO sentido (Lx,Lz): gira em torno do eixo horizontal
      // perpendicular, ancorado na base (origem local y=0)
      _qLean.setFromAxisAngle(_axis.set(Lz / mag, 0, -Lx / mag), mag);
      _qRot.setFromAxisAngle(_Y, baseRot[i]);
      dummy.quaternion.multiplyQuaternions(_qLean, _qRot);
    }
    dummy.updateMatrix();
    canopies[cLod[i]].setMatrixAt(cRow[i], dummy.matrix);
    trunk.setMatrixAt(i, dummy.matrix);
  };

  /**
   * Chamado a cada frame com as posições das pessoas (flat [x,z,x,z,...]).
   *
   * Duas cadências: a VARREDURA de contato roda a SCAN_HZ (o alvo do empurrão muda
   * devagar) e a MOLA integra a cada quadro, só nos arbustos ativos — é ela que dá
   * a fluidez. Sem ninguém por perto e sem arbusto voltando ao lugar, sai de graça.
   */
  let scanAcc = SCAN_DT; // primeira varredura já no primeiro quadro
  const update = (dt, people) => {
    scanAcc += dt;
    const scan = scanAcc >= SCAN_DT;
    // nada para fazer: nenhum arbusto mexido e ainda não é hora de varrer
    if (!scan && active.size === 0) return;

    if (scan) {
      scanAcc = 0;
      // 1) guarda o alvo anterior e zera o dos arbustos ativos
      for (const i of active) { prevX[i] = tgtX[i]; prevZ[i] = tgtZ[i]; tgtX[i] = 0; tgtZ[i] = 0; }
      // 2) acumula o empurrão de cada pessoa nos arbustos próximos
      for (let p = 0; p + 1 < people.length; p += 2) {
        const px = people[p], pz = people[p + 1];
        const ccx = Math.floor(px / CELL) - gx0, ccz = Math.floor(pz / CELL) - gz0;
        const cx1 = ccx + 1 < gCols ? ccx + 1 : gCols - 1;
        const cz1 = ccz + 1 < gRows ? ccz + 1 : gRows - 1;
        for (let cx = ccx > 0 ? ccx - 1 : 0; cx <= cx1; cx++) {
          const col = cx * gRows;
          for (let cz = ccz > 0 ? ccz - 1 : 0; cz <= cz1; cz++) {
            for (let j = gHead[col + cz]; j !== -1; j = gNext[j]) {
              const dx = baseX[j] - px, dz = baseZ[j] - pz;
              // rejeita pelo QUADRADO da distância: a raiz só é paga por quem
              // realmente encosta, que é a minoria esmagadora dos pares testados
              const d2 = dx * dx + dz * dz;
              if (d2 >= radius2[j] || d2 < 1e-6) continue;
              const dist = Math.sqrt(d2);
              if (!active.has(j)) { active.add(j); tgtX[j] = 0; tgtZ[j] = 0; prevX[j] = 0; prevZ[j] = 0; assentado[j] = 0; }
              const pm = (maxAngle[j] * (1 - dist / radius[j])) / dist; // pra longe da pessoa
              tgtX[j] += dx * pm;
              tgtZ[j] += dz * pm;
            }
          }
        }
      }
      // 3) o empurrão MUDOU? só então o arbusto volta a integrar. É aqui que a pessoa
      //    parada deixa de custar quadro: o alvo dela é idêntico a cada varredura.
      for (const i of active) {
        if ((tgtX[i] - prevX[i]) ** 2 + (tgtZ[i] - prevZ[i]) ** 2 > 1e-8) assentado[i] = 0;
      }
    }

    // 4) integra a mola e atualiza as matrizes dos arbustos ativos (todo quadro)
    let dirty = false;
    for (const i of active) {
      if (assentado[i]) continue;  // parado no equilíbrio e o empurrão não mudou
      let tx = tgtX[i], tz = tgtZ[i];
      const tm = Math.sqrt(tx * tx + tz * tz), ma = maxAngle[i];
      if (tm > ma) { tx *= ma / tm; tz *= ma / tm; }
      velX[i] += (SPRING_K * (tx - leanX[i]) - SPRING_C * velX[i]) * dt;
      velZ[i] += (SPRING_K * (tz - leanZ[i]) - SPRING_C * velZ[i]) * dt;
      leanX[i] += velX[i] * dt;
      leanZ[i] += velZ[i] * dt;
      writeMatrix(i);
      dirty = true;
      // ASSENTOU → sai da lista ativa. Duas formas de assentar, e antes só a primeira
      // contava: (a) sem empurrão e de volta ao repouso; (b) COM empurrão constante, já
      // parado na inclinação de equilíbrio.
      //
      // Faltava a (b) e ela é o caso do evento: uma pessoa PARADA dentro do raio de
      // contato de um arbusto mantém o alvo diferente de zero para sempre, então o
      // arbusto nunca saía de `active` — e enquanto houver UM ativo, o update marca
      // `needsUpdate` das TRÊS malhas inteiras. MEDIDO: 4.346 instâncias = 272 KB
      // reenviados POR QUADRO, 18.000 de 18.000 quadros, ~8 MB/s numa GPU integrada,
      // para vegetação que não está se mexendo. Agora o arbusto encostado numa pessoa
      // parada congela na inclinação de equilíbrio e para de custar quadro; qualquer
      // mudança do empurrão o reativa (quem escreve em tgtX/tgtZ o repõe em `active`).
      const emRepouso = tm < 1e-4 &&
        leanX[i] * leanX[i] + leanZ[i] * leanZ[i] < 2.25e-6 &&
        velX[i] * velX[i] + velZ[i] * velZ[i] < 2.25e-6;
      const noEquilibrio = tm >= 1e-4 &&
        (tx - leanX[i]) ** 2 + (tz - leanZ[i]) ** 2 < 2.25e-6 &&
        velX[i] * velX[i] + velZ[i] * velZ[i] < 2.25e-6;
      if (emRepouso) {
        leanX[i] = leanZ[i] = velX[i] = velZ[i] = 0;
        writeMatrix(i);
        toRemove.push(i);          // sem empurrão nenhum: sai da lista de ativos
      } else if (noEquilibrio) {
        leanX[i] = tx; leanZ[i] = tz; // crava na inclinação de equilíbrio
        velX[i] = velZ[i] = 0;
        writeMatrix(i);
        assentado[i] = 1;          // fica ativo (o empurrão existe) mas para de custar quadro
      }
    }
    for (let r = 0; r < toRemove.length; r++) active.delete(toRemove[r]);
    toRemove.length = 0;
    if (dirty) {
      canopies[0].instanceMatrix.needsUpdate = true;
      canopies[1].instanceMatrix.needsUpdate = true;
      trunk.instanceMatrix.needsUpdate = true;
    }
  };

  return { group, update };
}

/** Lóbulos que formam a copa (esferas sobrepostas). */
const CANOPY_LOBES = [
  { x: 0, y: 2.4, z: 0, r: 1.55 },
  { x: 1.05, y: 2.7, z: 0.2, r: 0.95 },
  { x: -1.0, y: 2.6, z: -0.25, r: 0.95 },
  { x: 0.2, y: 2.7, z: 1.05, r: 0.9 },
  { x: -0.25, y: 2.55, z: -1.05, r: 0.9 },
  { x: 0.1, y: 3.4, z: 0.0, r: 0.95 }
];
const CANOPY_DETAIL = 1;
/**
 * LOD DA COPA POR TAMANHO. Arbustos com escala abaixo deste limiar usam a copa de
 * `detail 0` (108 triângulos brutos, 91 após o culling) em vez de `detail 1`
 * (480 brutos, 303 após). São 603 dos 2.390: **125 mil triângulos, 12,5% do
 * cenário estático**.
 *
 * O que se ganha e o que se perde, medido: esses arbustos têm 17 px de mediana na
 * projeção real (p10 13, p90 27). A SILHUETA encolhe 0,4 px em média e 1,5 px no
 * pior caso — irrelevante. O que muda de verdade é o SOMBREADO: a faceta do lóbulo
 * grande dobra de tamanho, então a copa lê mais "quebrada" de perto.
 *
 * Botão único para reverter ou ajustar: `1` desliga o LOD (todas em detail 1),
 * `0.7` afeta só as realmente minúsculas.
 */
const LOD_SCALE = 0.9;

/**
 * Constrói a geometria da copa: várias esferas (lóbulos) mescladas, com cor
 * por vértice em tons de cinza (claro no topo) — multiplicada depois pelo
 * tint de cada árvore via instanceColor.
 *
 * PERFORMANCE: os lóbulos se sobrepõem MUITO (os 5 menores ficam quase todos
 * dentro do grande), então ~1/3 das faces da copa está ENTERRADA dentro de outro
 * lóbulo e nunca pode virar pixel — material opaco + depth test. `cullBuriedFaces`
 * remove exatamente essas faces (480 → 316 triângulos por árvore; com ~2.4k
 * árvores, −0,39M triângulos por frame). O critério é conservador (ver abaixo),
 * então a silhueta e o sombreado ficam IDÊNTICOS.
 */
function buildCanopyGeometry(detail = CANOPY_DETAIL) {
  const lobes = CANOPY_LOBES;
  const geos = lobes.map((l) => {
    const g = new THREE.IcosahedronGeometry(l.r, detail);
    g.translate(l.x, l.y, l.z);
    return g;
  });

  // bbox do conjunto COMPLETO: o gradiente vertical precisa dos mesmos minY/maxY
  // de antes, senão o degradê deslocaria (regressão visual sutil).
  const full = mergeGeometries(geos, false);
  full.computeBoundingBox();
  const minY = full.boundingBox.min.y;
  const maxY = full.boundingBox.max.y;

  const merged = cullBuriedFaces(geos, lobes, detail) || full;

  // Cor por vértice: gradiente vertical (base ~0.55 → topo ~1.0) + ruído leve
  const pos = merged.attributes.position;
  const colors = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i);
    const tnorm = (y - minY) / (maxY - minY);
    const shade = 0.55 + tnorm * 0.5 + (Math.random() - 0.5) * 0.06;
    const v = Math.min(1.1, shade);
    colors[i * 3] = v;
    colors[i * 3 + 1] = v;
    colors[i * 3 + 2] = v;
  }
  merged.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return merged;
}

/**
 * Remove as faces que estão ENTERRADAS dentro de outro lóbulo — invisíveis por
 * construção, logo sem nenhuma mudança de imagem.
 *
 * O teste é conservador de propósito. Um icosaedro subdividido é INSCRITO na sua
 * esfera: suas faces ficam mais perto do centro que o raio (a razão inradius/raio
 * é ~0.934 em detail=1). Se usássemos o raio da ESFERA, uma face "dentro da
 * esfera B" poderia ainda assim espetar para fora do SÓLIDO B e aparecer. Por
 * isso medimos o inradius REAL da malha e só descartamos uma face quando os TRÊS
 * vértices dela estão dentro do raio maciço de algum outro lóbulo — aí o triângulo
 * inteiro está dentro daquele sólido (esfera é convexa) e jamais rasteriza.
 *
 * Devolve null se nada pôde ser removido (o chamador usa a geometria original).
 */
function cullBuriedFaces(geos, lobes, detail) {
  // Planos de face de cada lóbulo, no espaço do MUNDO. Um icosaedro subdividido é
  // INSCRITO na sua esfera, então testar contra a esfera inscrita (o que esta
  // função fazia antes) descarta menos do que poderia: sobra a casca entre o
  // inradius e a superfície real. Testar contra os PLANOS DAS FACES usa o sólido
  // exato — 316 → 303 triângulos por copa, mais 31.070 na cidade (3,11%), com a
  // mesma garantia de invisibilidade.
  const planos = lobes.map((L) => facePlanes(L, detail));

  const posOut = [];
  const nrmOut = [];
  const EPS = 1e-4; // margem contra erro de ponto flutuante
  for (let gi = 0; gi < geos.length; gi++) {
    const p = geos[gi].attributes.position;
    const n = geos[gi].attributes.normal;
    for (let i = 0; i < p.count; i += 3) {
      let buried = false;
      for (let li = 0; li < lobes.length && !buried; li++) {
        if (li === gi) continue;
        const P = planos[li];
        // dentro do sólido = do lado interno de TODOS os planos de face
        let all = true;
        for (let k = 0; k < 3 && all; k++) {
          const x = p.getX(i + k), y = p.getY(i + k), z = p.getZ(i + k);
          for (let f = 0; f < P.length; f += 4) {
            if (P[f] * x + P[f + 1] * y + P[f + 2] * z > P[f + 3] - EPS) { all = false; break; }
          }
        }
        buried = all;
      }
      if (buried) continue;
      for (let k = 0; k < 3; k++) {
        posOut.push(p.getX(i + k), p.getY(i + k), p.getZ(i + k));
        nrmOut.push(n.getX(i + k), n.getY(i + k), n.getZ(i + k));
      }
    }
  }
  if (!posOut.length) return null;

  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(new Float32Array(posOut), 3));
  out.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(nrmOut), 3));
  // sem `uv`: o material da copa é vertexColors puro (nenhum mapa) — o atributo
  // seria peso morto em memória e no upload.
  return out;
}

/**
 * Planos das faces de um lóbulo, em coordenadas de MUNDO, como [nx,ny,nz,d,...]
 * onde `n·x <= d` significa "do lado de dentro". Como o icosaedro é convexo,
 * estar do lado interno de todos os planos é exatamente estar dentro do sólido.
 */
function facePlanes(L, detail) {
  const p = new THREE.IcosahedronGeometry(L.r, detail).attributes.position;
  const out = [];
  const vistos = new Set();
  for (let i = 0; i < p.count; i += 3) {
    const ax = p.getX(i), ay = p.getY(i), az = p.getZ(i);
    const bx = p.getX(i + 1) - ax, by = p.getY(i + 1) - ay, bz = p.getZ(i + 1) - az;
    const cx = p.getX(i + 2) - ax, cy = p.getY(i + 2) - ay, cz = p.getZ(i + 2) - az;
    let nx = by * cz - bz * cy, ny = bz * cx - bx * cz, nz = bx * cy - by * cx;
    const len = Math.hypot(nx, ny, nz);
    if (len < 1e-12) continue;
    nx /= len; ny /= len; nz /= len;
    // orienta para FORA (o centro do lóbulo está na origem desta geometria)
    if (nx * ax + ny * ay + nz * az < 0) { nx = -nx; ny = -ny; nz = -nz; }
    // deduplica planos coincidentes (faces coplanares) para encurtar o laço
    const key = `${nx.toFixed(5)},${ny.toFixed(5)},${nz.toFixed(5)}`;
    if (vistos.has(key)) continue;
    vistos.add(key);
    // desloca o plano para o mundo: n·(x - centro) <= d_local
    const dLocal = nx * ax + ny * ay + nz * az;
    out.push(nx, ny, nz, dLocal + (nx * L.x + ny * L.y + nz * L.z));
  }
  return out;
}

/**
 * Menor distância do centro ao plano de uma face, para uma esfera de raio 1 —
 * ou seja, o raio da maior esfera 100% contida na malha. Medido na geometria
 * REAL (não numa constante) para o teste de oclusão continuar correto se o
 * `detail` da copa mudar.
 */
function inradiusRatio(detail) {
  const p = new THREE.IcosahedronGeometry(1, detail).attributes.position;
  let min = Infinity;
  for (let i = 0; i < p.count; i += 3) {
    const ax = p.getX(i), ay = p.getY(i), az = p.getZ(i);
    const bx = p.getX(i + 1) - ax, by = p.getY(i + 1) - ay, bz = p.getZ(i + 1) - az;
    const cx = p.getX(i + 2) - ax, cy = p.getY(i + 2) - ay, cz = p.getZ(i + 2) - az;
    let nx = by * cz - bz * cy, ny = bz * cx - bx * cz, nz = bx * cy - by * cx;
    const len = Math.hypot(nx, ny, nz) || 1;
    min = Math.min(min, Math.abs((nx * ax + ny * ay + nz * az) / len));
  }
  return min;
}
